import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { ModalService } from '@ops/shared-types';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'ops-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class DatePickerComponent implements OnInit {
  @Input() id: string;
  @Input() parentId: string;
  @Input() isMultiSelect: boolean;
  @Input() selectedDates: Array<Date | undefined | null>;
  @Input() startingFocusIndex: number;

  @Output() datesSelected: EventEmitter<Array<Date>> = new EventEmitter<
    Array<Date>
  >();
  @Output() clearSelectedDates: EventEmitter<void> = new EventEmitter<void>();

  //should add localization, centralize
  private monthNames = [
    'january',
    'february',
    'march',
    'april',
    'may',
    'june',
    'july',
    'august',
    'september',
    'october',
    'november',
    'december',
  ];

  //date.Day is irrelevant
  monthDisplayed: { date: Date; name: string };
  daysDisplayed: Array<Date>;
  dayNames: Array<string> = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
  ];

  private currentDate: Date;
  private element: HTMLElement | null;
  private isOpen: boolean = false;
  private currentFocusId: number;
  private lastSelectedDate: Date | null = null; // Anchor date for shift-click range selection

  constructor(
    private modalService: ModalService, 
    private el: ElementRef,
    private translateService: TranslateService
  ) {
    this.element = this.el.nativeElement;
  }

  //starts with sunday, should have an @Input to custommize starting day
  ngOnInit(): void {
    if (!this.id) {
      console.error('modal must have an id');
      return;
    }
    if (!this.startingFocusIndex) {
      this.startingFocusIndex = 0;
    }
    if (!this.selectedDates) {
      this.selectedDates = [];
    }
    this.currentFocusId = this.startingFocusIndex;
    this.currentDate = new Date();
    this.currentDate.setHours(0);
    this.currentDate.setMinutes(0);
    this.currentDate.setSeconds(0);
    this.currentDate.setMilliseconds(0);
    
    // Add retry mechanism for parent element attachment
    this.attachToParent();
    this.modalService.add(this);
    this.changeDisplayedMonth(this.currentDate);
    if (this.element) this.element.style.display = 'none';
  }

  private attachToParent(): void {
    const parent = document.querySelector(`#${this.parentId}`);
    if (this.element && parent) {
      parent.appendChild(this.element);
    } else {
      // Retry with multiple attempts if parent element is not found
      this.retryAttachToParent(0);
    }
  }

  private retryAttachToParent(attempt: number): void {
    const maxAttempts = 10;
    const retryDelay = 50;
    
    if (attempt >= maxAttempts) {
      console.warn(`Failed to attach date picker ${this.id} to parent ${this.parentId} after ${maxAttempts} attempts`);
      return;
    }
    
    setTimeout(() => {
      const retryParent = document.querySelector(`#${this.parentId}`);
      if (this.element && retryParent) {
        retryParent.appendChild(this.element);
      } else {
        this.retryAttachToParent(attempt + 1);
      }
    }, retryDelay);
  }

  ngOnDestroy(): void {
    this.modalService.remove(this.id);
    this.isOpen = false;
    this.element?.remove();
  }

  open(): void {
    // Ensure date picker is properly attached before opening
    this.ensureAttachedToParent();
    
    if (this.element) {
      this.element.style.display = 'flex';
      this.element.style.position = 'absolute';
      this.element.style.top = '0';
      this.isOpen = true;
    }
  }

  private ensureAttachedToParent(): void {
    const parent = document.querySelector(`#${this.parentId}`);
    if (this.element && parent && !parent.contains(this.element)) {
      parent.appendChild(this.element);
    }
  }

  closeFromBackground(event: any) {
    event.stopPropagation();
    this.close();
  }

  close(): void {
    this.isOpen = false;
    if (this.element) this.element.style.display = 'none';
  }

  //TODO: revise this, maybe using services or the module to keep state/shared collection is better
  onSelectDate(value: Date, event?: any): void {
    event?.stopPropagation();
    
    // Handle shift-click range selection
    if (event?.shiftKey && this.lastSelectedDate) {
      this.selectDateRange(this.lastSelectedDate, value);
      this.datesSelected.emit(this.selectedDates as Date[]);
      return;
    }
    
    // Update anchor date for future shift-click operations
    this.lastSelectedDate = new Date(value);
    
    if (this.isMultiSelect === true) {
      this.toggleDate(value);
      this.datesSelected.emit(this.selectedDates as Date[]);
      return;
    }
    this.selectedDates = [new Date(value)];
    this.datesSelected.emit(this.selectedDates as Date[]);
    this.modalService.close(this.id);
  }

  onClearSelection() {
    this.selectedDates = [];
    this.lastSelectedDate = null; // Reset anchor date
    this.datesSelected.emit(this.selectedDates as Date[]);
    this.close();
  }

  /**
   * Selects all dates between startDate and endDate (inclusive)
   */
  private selectDateRange(startDate: Date, endDate: Date): void {
    let start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    
    let end = new Date(endDate);
    end.setHours(0, 0, 0, 0);
    
    // Ensure start is before end
    if (start.getTime() > end.getTime()) {
      [start, end] = [end, start];
    }
    
    // Generate all dates in the range
    const datesInRange: Date[] = [];
    const current = new Date(start);
    
    while (current.getTime() <= end.getTime()) {
      datesInRange.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    // Add all dates in range to selection (avoid duplicates)
    datesInRange.forEach(date => {
      if (!this.isDateInArray(this.selectedDates as Date[], date)) {
        this.selectedDates.push(new Date(date));
      }
    });
    
    // Sort the selected dates
    this.selectedDates = [
      ...this.selectedDates
        .map((d: any) => (d = new Date(d)))
        .sort((a, b) => a.getTime() - b.getTime()),
    ];
  }

  private toggleDate(newDate: Date) {
    if (this.isDateInArray(this.selectedDates as Date[], newDate)) {
      this.selectedDates = this.selectedDates.filter(
        (x: any) => new Date(x).getTime() != new Date(newDate).getTime()
      );
      this.selectedDates = [
        ...this.selectedDates
          .map((d: any) => (d = new Date(d)))
          .sort((a, b) => a.getTime() - b.getTime()),
      ];
      return;
    }
    this.selectedDates.push(newDate);
    this.selectedDates = [
      ...this.selectedDates
        .map((d: any) => (d = new Date(d)))
        .sort((a, b) => a.getTime() - b.getTime()),
    ];
  }

  shiftMonthUp(shiftUp: boolean) {
    //.getMonth() returns months starting with 1,
    //new Date() months start with 0
    //so we substrace 1 from what is needed
    let newMonth = shiftUp
      ? this.monthDisplayed.date.getMonth() + 1
      : this.monthDisplayed.date.getMonth() - 1;
    //new Date() 3rd parameter is handled so that 2 results in the first day of the month
    this.changeDisplayedMonth(
      new Date(this.monthDisplayed.date.getFullYear(), newMonth, 2)
    );
  }

  onKeyAction(key: any, value: any) {
    if (key.keyCode !== 32 && key.keyCode !== 13) {
      //enter and space keys
      return;
    }
    if (value instanceof Date) {
      this.onSelectDate(value as Date);
      document
        .getElementById(this.currentFocusId.toString())
        ?.classList.add('selected');
      return;
    }
    this.shiftMonthUp(value as boolean);
  }

  changeDisplayedMonth(newDate: Date): void {
    this.monthDisplayed = {
      date: newDate,
      name: this.monthNames[newDate.getMonth()],
    };
    this.daysDisplayed = this.generateDays(
      newDate.getFullYear(),
      newDate.getMonth()
    );
  }

  // Format month with proper Japanese formatting
  formatMonth(monthName: string): string {
    // Handle undefined or null monthName
    if (!monthName) {
      return '';
    }
    
    const currentLang = this.translateService.currentLang;
    if (currentLang === 'jp') {
      // For Japanese, we need to get the month number and format it
      const monthIndex = this.monthNames.findIndex(m => m === monthName);
      if (monthIndex !== -1) {
        const monthNumber = monthIndex + 1;
        return `${monthNumber}${this.translateService.instant('dateFormatting.monthFormat')}`;
      }
    }
    // For English and other languages, capitalize the first letter
    return monthName.charAt(0).toUpperCase() + monthName.slice(1);
  }

  // Format year with proper Japanese formatting
  formatYear(year: number): string {
    // Handle undefined or null year
    if (!year) {
      return '';
    }
    
    const currentLang = this.translateService.currentLang;
    if (currentLang === 'jp') {
      return `${year}${this.translateService.instant('dateFormatting.yearFormat')}`;
    }
    return year.toString();
  }

  // Format combined month and year for header
  formatMonthYear(monthName: string, year: number): string {
    // Handle undefined or null values
    if (!monthName || !year) {
      return '';
    }
    
    const currentLang = this.translateService.currentLang;
    if (currentLang === 'jp') {
      const monthIndex = this.monthNames.findIndex(m => m === monthName);
      if (monthIndex !== -1) {
        const monthNumber = monthIndex + 1;
        return `${year}${this.translateService.instant('dateFormatting.yearFormat')}${monthNumber}${this.translateService.instant('dateFormatting.monthFormat')}`;
      }
    }
    // For English and other languages, capitalize the month and format properly
    const capitalizedMonth = monthName.charAt(0).toUpperCase() + monthName.slice(1);
    return `${capitalizedMonth} ${year}`;
  }

  private generateDays(year: number, month: number): Array<Date> {
    let result: Array<Date> = [];
    //new Date() 3rd parameter when 0 instantiates last date of previous month
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    result.push(...this.getDaysPreviousMonthToDisplay(year, month));
    for (let i = 0; i < daysInMonth; i++) {
      result.push(new Date(year, month, i + 1));
    }
    result.push(
      ...this.getDaysInFollowingMonthToDisplay(year, month, daysInMonth)
    );
    return result;
  }

  private getDaysPreviousMonthToDisplay(
    year: number,
    month: number
  ): Array<Date> {
    let result: Array<Date> = [];
    //getDate() starts with 0=Sunday
    const firstDayWeekDay = new Date(year, month, 1).getDay();
    if (firstDayWeekDay != 0) {
      const daysInLastMonth = new Date(year, month, 0).getDate();
      for (
        let i = daysInLastMonth - firstDayWeekDay;
        i < daysInLastMonth;
        i++
      ) {
        result.push(new Date(year, month - 1, i + 1));
      }
    }
    return result;
  }

  private getDaysInFollowingMonthToDisplay(
    year: number,
    month: number,
    daysInMonth: number
  ): Array<Date> {
    let result: Array<Date> = [];
    const lastDayWeekDay = new Date(year, month, daysInMonth).getDay();
    if (lastDayWeekDay != 7) {
      for (let i = 1; i < 7 - lastDayWeekDay; i++) {
        result.push(new Date(year, month + 1, i));
      }
    }
    return result;
  }

  //TODO: move this to utilities and refactor so that it doesnt get called milions of times
  private isDateInArray(array: Array<Date>, value: Date): boolean {
    const d = new Date(value);
    if (!array || array.length == 0) {
      return false;
    }
    if (!array?.length) {
      array = new Array<Date>();
    }
    if (array && d) {
      const l = !!(array as Array<Date>).find(
        (item: Date) => new Date(item)?.getTime() === d?.getTime()
      );
      return l;
    }
    return false;
  }

  @HostListener('document:keyup', ['$event']) onKeyUp(e: any) {
    if (!this.isOpen) return;
    if (e.keyCode === 9) {
      this.modalService.close(this.id);
      return;
    }
    let internalIndex = this.currentFocusId - this.startingFocusIndex;
    //TODO: refactor into smaller methods
    switch (e.keyCode) {
      case 37: {
        //LEFT arrow
        if (internalIndex > 1) {
          this.currentFocusId--;
          break;
        }
        this.currentFocusId =
          this.startingFocusIndex + this.daysDisplayed.length + 2;
        break;
      }
      case 39: {
        //RIGHT arrow
        if (internalIndex < this.daysDisplayed.length + 2) {
          this.currentFocusId++;
          break;
        }
        this.currentFocusId = this.startingFocusIndex + 1;
        break;
      }
      case 40: {
        //DOWN arrow
        if (internalIndex === 1) {
          //month navigation arrow icon - left
          this.currentFocusId += 2;
          break;
        }
        if (internalIndex === this.daysDisplayed.length + 2 - 6) {
          //first day of last week + 2 for arrow icons
          this.currentFocusId = this.startingFocusIndex + 1;
          break;
        }
        if (internalIndex === this.daysDisplayed.length + 2) {
          //last day of last week + 2 for arrow icons
          this.currentFocusId = this.startingFocusIndex + 2;
          break;
        }
        if (internalIndex > this.daysDisplayed.length + 2 - 7) {
          // any other day of last week
          this.currentFocusId -= this.daysDisplayed.length - 7;
          break;
        }
        this.currentFocusId += 7;
        break;
      }
      case 38: {
        //UP arrow
        if (internalIndex === 1) {
          //navigation arrows - left
          this.currentFocusId =
            this.startingFocusIndex + this.daysDisplayed.length + 2 - 6;
          break;
        }
        if (internalIndex === 2) {
          //navigation arrows - right
          this.currentFocusId =
            this.startingFocusIndex + this.daysDisplayed.length + 2;
          break;
        }
        if (internalIndex === 3) {
          //first day of first week
          this.currentFocusId -= 2;
          break;
        }
        if (internalIndex < 9) {
          //other days of first week
          this.currentFocusId += this.daysDisplayed.length;
        }
        this.currentFocusId -= 7;
        break;
      }
    }
    document.getElementById(this.currentFocusId?.toString())?.focus();
  }
}
